import React, { FC, FormHTMLAttributes, MutableRefObject } from 'react';

import type { IRichText, ITestable } from '@belong/types';
import { ALERT_LEVELS, ALERT_POSITION } from '@belong/types';
import { Layout, Stack, Alert, RichText } from '../../..';
import { TransparentOverlay } from '../common/styles';

interface IGenericFormLayout extends ITestable, Omit<FormHTMLAttributes<HTMLFormElement>, 'ref' | 'onSubmit'> {
  alertMessage?: IRichText;
  isLoading?: boolean;
  FormRef: MutableRefObject<HTMLFormElement | null>;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  validateForm?: () => Promise<boolean>;
}

export const GenericFormLayout: FC<React.PropsWithChildren<IGenericFormLayout>> = ({
  alertMessage,
  children,
  'data-testid': testId,
  FormRef,
  isLoading,
  handleSubmit,
  validateForm,
  ...formProps
}) => {
  const onSubmit: React.FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    if (validateForm) {
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }
    }
    handleSubmit(e);
  };

  return (
    <Layout data-testid={testId}>
      {isLoading && <TransparentOverlay />}
      <Stack space={{ xs: 'large', lg: 'xlarge' }}>
        {alertMessage && (
          <Alert position={ALERT_POSITION.BLOCK} type={ALERT_LEVELS.ERROR} data-testid={testId && `${testId}-alert`}>
            <RichText {...alertMessage} />
          </Alert>
        )}
        <form method="POST" onSubmit={onSubmit} ref={FormRef} {...formProps} noValidate>
          <Stack space={{ xs: 'large', lg: 'xlarge' }}>{children}</Stack>
        </form>
      </Stack>
    </Layout>
  );
};

GenericFormLayout.displayName = 'GenericFormLayout';
